import { type SearchRequest, VIEW_KINDS, type View, type ViewKind } from '@servicetim/shared';
import { useCreateView, useUpdateView } from '@servicetim/ui/api';
import {
  Button,
  Checkbox,
  Input,
  ProblemBanner,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@servicetim/ui/components';
import { useEffect, useId, useState } from 'react';
import type { FieldCatalog } from '../projects/catalog/field-catalog';
import { fieldErrorsOf, messageAt } from '../projects/lib/field-errors';
import { FormRow } from '../projects/ui/form-row';
import { InlineErrors } from '../projects/ui/inline-errors';
import { SimpleSelect } from '../projects/ui/simple-select';
import { projectQuery, QueryBuilder, validateQuery } from './query-builder/query-builder';
import { RunPreview } from './run-preview';

export const VIEW_KIND_LABELS: Record<ViewKind, string> = {
  list: 'List',
  table: 'Table',
  board: 'Board',
  calendar: 'Calendar',
  timeline: 'Timeline',
};

export interface ViewEditorSheetProps {
  projectId: string;
  /** `null` creates a view; `'new'` is not used — pass `undefined` to keep closed. */
  view: View | null | undefined;
  catalog: FieldCatalog;
  onOpenChange: (open: boolean) => void;
}

/** Name, kind, sharing flags, the query builder and a "Run" preview; saves via create / update. */
export function ViewEditorSheet({ projectId, view, catalog, onOpenChange }: ViewEditorSheetProps) {
  const id = useId();
  const create = useCreateView();
  const update = useUpdateView();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ViewKind>('table');
  const [shared, setShared] = useState(false);
  const [portalVisible, setPortalVisible] = useState(false);
  const [position, setPosition] = useState(0);
  const [query, setQuery] = useState<SearchRequest>(projectQuery(projectId));
  const [clientIssues, setClientIssues] = useState<{ path: string; message: string }[]>([]);
  const open = view !== undefined;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when opened or the row changes
  useEffect(() => {
    if (!open) return;
    setName(view?.name ?? '');
    setKind(view?.kind ?? 'table');
    setShared(view?.shared ?? false);
    setPortalVisible(view?.portalVisible ?? false);
    setPosition(view?.position ?? 0);
    setQuery(view?.query ?? projectQuery(projectId));
    setClientIssues([]);
    create.reset();
    update.reset();
  }, [open, view?.id, projectId]);

  const error = create.error ?? update.error;
  const serverErrors = fieldErrorsOf(error);
  const queryErrors = [...serverErrors.filter((e) => e.path.startsWith('query')), ...clientIssues];

  const save = async () => {
    const issues = validateQuery(query, catalog);
    setClientIssues(
      issues.map((i) => ({ path: `query.${i.path}`, message: `${i.code}: ${i.message}` })),
    );
    if (issues.length > 0 || name.trim() === '') return;
    try {
      if (view) {
        await update.mutateAsync({
          id: view.id,
          patch: { name: name.trim(), kind, shared, portalVisible, position, query },
        });
        toast.success(`View "${name.trim()}" saved`);
      } else {
        await create.mutateAsync({
          projectId,
          name: name.trim(),
          kind,
          shared,
          portalVisible,
          position,
          query,
        });
        toast.success(`View "${name.trim()}" created`);
      }
      onOpenChange(false);
    } catch {
      // banner
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle>{view ? `Edit view "${view.name}"` : 'New view'}</SheetTitle>
          <SheetDescription>
            A saved search: filter, sort and grouping over the field registry. Shared views need{' '}
            <code className="font-mono">view.manage_shared</code>;{' '}
            <code className="font-mono">$me</code> resolves per viewer.
          </SheetDescription>
        </SheetHeader>
        <form
          className="flex-1 space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          {error && queryErrors.length === 0 ? (
            <ProblemBanner error={error} size="sm" omitPaths={serverErrors.map((e) => e.path)} />
          ) : null}
          <div className="grid gap-4 sm:grid-cols-[1fr_10rem_6rem]">
            <FormRow
              label="Name"
              htmlFor={`${id}-name`}
              required
              error={messageAt(serverErrors, 'name')}
            >
              <Input
                id={`${id}-name`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                autoFocus
              />
            </FormRow>
            <FormRow label="Kind" htmlFor={`${id}-kind`}>
              <SimpleSelect
                id={`${id}-kind`}
                value={kind}
                onChange={(next) => next && setKind(next as ViewKind)}
                options={VIEW_KINDS.map((k) => ({ value: k, label: VIEW_KIND_LABELS[k] }))}
              />
            </FormRow>
            <FormRow label="Position" htmlFor={`${id}-position`}>
              <Input
                id={`${id}-position`}
                type="number"
                min={0}
                value={position}
                onChange={(e) => setPosition(Math.max(0, Number(e.target.value) || 0))}
              />
            </FormRow>
          </div>
          <div className="flex flex-wrap gap-6">
            <FormRow label="Shared with the project" htmlFor={`${id}-shared`} inline>
              <Checkbox
                id={`${id}-shared`}
                checked={shared}
                onCheckedChange={(next) => setShared(next === true)}
              />
            </FormRow>
            <FormRow label="Visible in the portal" htmlFor={`${id}-portal`} inline>
              <Checkbox
                id={`${id}-portal`}
                checked={portalVisible}
                onCheckedChange={(next) => setPortalVisible(next === true)}
              />
            </FormRow>
          </div>
          <QueryBuilder
            value={query}
            onChange={setQuery}
            catalog={catalog}
            kind={kind}
            idPrefix={`${id}-q`}
          />
          <InlineErrors errors={queryErrors} prefix="query." />
          <RunPreview query={query} />
          <SheetFooter className="flex-row justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={create.isPending || update.isPending || name.trim() === ''}
            >
              {view ? 'Save view' : 'Create view'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
